/**
 * The quiet line under a finished reply, Cursor's: copy, export, retry (last
 * reply only), when it was written and which model wrote it. The actions only
 * show while the reply is hovered or focused.
 */
import { Check, Copy, FileDown, RefreshCw } from "lucide-react";
import {
  memo,
  useState,
  useSyncExternalStore,
  type FC,
  type ReactNode
} from "react";

import { Button } from "@/components/ui/button";
import { MetaLine } from "@/components/ui/meta-line";
import type { ModelUsed } from "@/features/agents/types";
import { cn } from "@/lib/utils";

import { copyRichText } from "../replyExport";
import { ExportReplyDialog } from "./ExportReplyDialog";
import { ModelUsedLabel } from "./ModelUsedLabel";

const DAY_MS = 24 * 60 * 60 * 1000;
const TICK_MS = 30_000;

/** "now", "7m ago", the clock time today, "Yesterday 4:05 PM", else the date. */
export const agoLabel = (ms: number, now: number = Date.now()): string => {
  const minutes = Math.floor(Math.max(0, now - ms) / 60000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m ago`;
  const at = new Date(ms);
  const clock = at.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  if (ms >= today.getTime()) return clock;
  if (ms >= today.getTime() - DAY_MS) return `Yesterday ${clock}`;
  return at.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(at.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {})
  });
};

/** One shared timer for every footer on screen, so "3m ago" keeps moving. */
const ticker = (() => {
  const listeners = new Set<() => void>();
  let timer: number | null = null;
  let now = Date.now();
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      if (timer === null) {
        now = Date.now();
        timer = window.setInterval(() => {
          now = Date.now();
          listeners.forEach((l) => l());
        }, TICK_MS);
      }
      return () => {
        listeners.delete(listener);
        if (!listeners.size && timer !== null) {
          window.clearInterval(timer);
          timer = null;
        }
      };
    },
    now: () => now
  };
})();

const noSubscribe = () => () => undefined;
const never = () => 0;

const useMinuteClock = (on: boolean): number =>
  useSyncExternalStore(
    on ? ticker.subscribe : noSubscribe,
    on ? ticker.now : never
  );

type Props = {
  /** The reply's markdown, for Copy; nothing to copy hides the button. */
  markdown: string;
  /** Epoch ms the reply was written, when known. */
  writtenAt?: number | null;
  model?: ModelUsed | null;
  /**
   * Only the last reply can be retried; `model` re-runs it on that model
   * (the primary a fallback stood in for).
   */
  onRetry?: (options?: { model?: string }) => void;
  retryDisabled?: boolean;
  /** Extra actions the host adds (the workspace's "Open as report"). */
  children?: ReactNode;
  /** A case chat: Export can also save the reply to Work Products. */
  canSaveToWorkProducts?: boolean;
  className?: string;
};

export const AssistantFooter: FC<Props> = memo(
  ({
    markdown,
    writtenAt,
    model,
    onRetry,
    retryDisabled,
    children,
    canSaveToWorkProducts,
    className
  }) => {
    const [copied, setCopied] = useState(false);
    const [exporting, setExporting] = useState(false);
    // Mounted on first use only: a long thread has a footer per reply.
    const [exportMounted, setExportMounted] = useState(false);
    const copy = async () => {
      try {
        await copyRichText(markdown);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      } catch {
        /* clipboard unavailable */
      }
    };
    const now = useMinuteClock(Boolean(writtenAt));
    const stamp = writtenAt
      ? agoLabel(writtenAt, Math.max(now, writtenAt))
      : null;
    if (!markdown && !onRetry && !children && !stamp && !model) return null;
    return (
      <div
        className={cn("-ml-1 flex h-6 items-center gap-1.5", className)}
        data-testid="assistant-footer"
      >
        {markdown || onRetry || children ? (
          <span className="flex items-center gap-0.5">
            {markdown ? (
              <Button
                variant="ghost"
                size="icon-xs"
                className="text-fg-4 hover:text-fg"
                onClick={() => void copy()}
                aria-label={copied ? "Copied" : "Copy"}
                title={copied ? "Copied" : "Copy"}
                data-testid="message-copy"
              >
                {copied ? <Check /> : <Copy />}
              </Button>
            ) : null}
            {markdown ? (
              <Button
                variant="ghost"
                size="icon-xs"
                className={cn(
                  "text-fg-4 hover:text-fg",
                  exporting && "bg-fill-2 text-fg"
                )}
                onClick={() => {
                  setExportMounted(true);
                  setExporting(true);
                }}
                aria-label="Export"
                title="Export"
                data-testid="message-export"
              >
                <FileDown />
              </Button>
            ) : null}
            {onRetry ? (
              <Button
                variant="ghost"
                size="icon-xs"
                className="text-fg-4 hover:text-fg"
                onClick={() => onRetry()}
                disabled={retryDisabled}
                aria-label="Retry"
                title="Retry"
                data-testid="message-retry"
              >
                <RefreshCw />
              </Button>
            ) : null}
            {children}
          </span>
        ) : null}
        <MetaLine
          className="text-fg-4"
          parts={[
            stamp ? (
              <time
                dateTime={new Date(writtenAt as number).toISOString()}
                title={new Date(writtenAt as number).toLocaleString()}
                data-testid="message-time"
              >
                {stamp}
              </time>
            ) : null,
            model ? <ModelUsedLabel used={model} className="text-fg-4" /> : null
          ]}
        />
        {onRetry && model?.fallback && model.primary_model ? (
          <Button
            variant="link"
            size="xs"
            className="text-sm text-fg-3"
            disabled={retryDisabled}
            onClick={() => onRetry({ model: model.primary_model?.id })}
            data-testid="message-rerun-primary"
          >
            Re-run on {model.primary_model.label}
          </Button>
        ) : null}
        {exportMounted && markdown ? (
          <ExportReplyDialog
            open={exporting}
            onOpenChange={setExporting}
            markdown={markdown}
            canSaveToWorkProducts={canSaveToWorkProducts}
          />
        ) : null}
      </div>
    );
  }
);
AssistantFooter.displayName = "AssistantFooter";
